/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { containerService } from './api';

export interface ContainerStatusEvent {
  type: 'container_status';
  channel: string;
  container_id: string;
  name: string;
  status: string;
  helm_status?: string;
  failure_reason?: string; // present when status is "failed"
  ts: string;
}

type EventHandler = (ev: ContainerStatusEvent) => void;
type StateHandler = (connected: boolean) => void;

class ContainerEventsClient {
  private ws: WebSocket | null = null;
  private projectId: string | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 20;
  private readonly maxReconnectDelay = 30000;
  private handlers = new Set<EventHandler>();
  private stateHandlers = new Set<StateHandler>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  connect(projectId: string) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.projectId === projectId) return;
    this.disconnect();
    this.projectId = projectId;
    this.reconnectAttempts = 0;
    this.open();
  }

  private open() {
    if (!this.projectId) return;
    try {
      this.ws = new WebSocket(containerService.getEventsUrl(this.projectId));
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.ws?.send(JSON.stringify({ action: 'subscribe', channel: `project:${this.projectId}:containers` }));
      this.emitState(true);
    };
    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string);
        if (msg.type === 'container_status') {
          this.handlers.forEach((h) => h(msg as ContainerStatusEvent));
        }
      } catch {
        // ignore malformed frames
      }
    };
    this.ws.onclose = (event) => {
      this.ws = null;
      this.emitState(false);
      if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      }
    };
    this.ws.onerror = () => {
      // onclose follows; reconnect handled there
    };
  }

  private scheduleReconnect() {
    const delay = Math.min(1000 * 2 ** this.reconnectAttempts, this.maxReconnectDelay);
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  disconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close(1000);
      this.ws = null;
    }
    this.emitState(false);
  }

  onEvent(h: EventHandler): () => void {
    this.handlers.add(h);
    return () => this.handlers.delete(h);
  }

  onStateChange(h: StateHandler): () => void {
    this.stateHandlers.add(h);
    return () => this.stateHandlers.delete(h);
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private emitState(connected: boolean) {
    this.stateHandlers.forEach((h) => h(connected));
  }
}

export const containerEventsClient = new ContainerEventsClient();
